/* eslint-disable complexity -- Durable stream orchestration keeps one epoch-owned state machine. */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { showToast } from 'components/ui/toaster'
import {
  ChatStreamProtocolError,
  cancelChatTurn,
  createChatTurn,
  fetchActiveChatTurn,
  getChatApiErrorStatus,
  streamChatTurnEvents,
} from 'modules/coaching/api'
import {
  applyAgentMessageSaved,
  applyContentBlockAdded,
  applyTextDelta,
  applyThinkingEvent,
  createAgentMessage,
  createChatStreamSession,
  generateTemporaryMessageId,
} from 'modules/coaching/store/chat-stream-reducer'
import { AgentMessage } from 'modules/coaching/types'
import {
  getMessageSnapshotKey,
  getReconnectDelay,
  shouldAdoptPersistedMessages,
  waitForReconnect,
} from 'modules/coaching/utils/agentInteractionRuntime'
import { webLogger } from 'utils/logger'

import type { components } from '@startech/openapi'
import type { ChatStreamSession, SetMessages } from 'modules/coaching/store/chat-stream-reducer'
type ChatVideoContext = components['schemas']['ChatVideoContext']
const EMPTY_AGENT_MESSAGES: AgentMessage[] = []
const HTTP_STATUS_UNAUTHORIZED = 401
const HTTP_STATUS_NOT_FOUND = 404
const HTTP_STATUS_CONFLICT = 409
export interface UseAgentInteractionOptions {
  conversationId?: string
  initialMessages?: AgentMessage[]
  onChatComplete?: () => void
  viewerKey?: string
}
export type AgentInteractionPhase =
  'idle' | 'restoring' | 'creating' | 'connecting' | 'streaming' | 'reconnecting' | 'stopping'
export interface UseAgentInteractionResult {
  canStop: boolean
  messages: AgentMessage[]
  isProcessing: boolean
  isRestoring: boolean
  isStopping: boolean
  phase: AgentInteractionPhase
  error?: string
  sendMessage: (text: string, options?: { videoContext?: ChatVideoContext }) => Promise<void>
  addMessage: (message: AgentMessage) => void
  clearConversation: () => void
  stopResponse: () => Promise<void>
}
type MessageMutation = (messages: AgentMessage[]) => AgentMessage[]
type TemporalSession = ChatStreamSession & {
  controller?: AbortController
  epoch: number
  lastAppliedSequence: number
  optimisticUserMessageId?: string
  pendingError?: string
  rafId?: number
  stopPromise?: Promise<void>
  terminal: boolean
  turnId: string
}
const removeTemporaryAssistantOutput = (messages: AgentMessage[], session: ChatStreamSession): AgentMessage[] => {
  const thinkingIds = new Set(session.thinkingMessageIds)
  return messages.filter(message => message.id !== session.streamingAgentMessageId && !thinkingIds.has(message.id))
}
export const useAgentInteraction = (options: UseAgentInteractionOptions): UseAgentInteractionResult => {
  const initialMessages = options.initialMessages ?? EMPTY_AGENT_MESSAGES
  const initialMessagesSnapshotKey = useMemo(() => getMessageSnapshotKey(initialMessages), [initialMessages])
  const initialMessagesReference = useRef(initialMessages)
  initialMessagesReference.current = initialMessages
  const [messages, setMessages] = useState<AgentMessage[]>(initialMessages)
  const [phase, setPhase] = useState<AgentInteractionPhase>('restoring')
  const [error, setError] = useState<string | undefined>()
  const epochReference = useRef(0)
  const conversationIdReference = useRef(options.conversationId)
  conversationIdReference.current = options.conversationId
  const sessionReference = useRef<TemporalSession | undefined>(undefined)
  const pendingMutationsReference = useRef<MessageMutation[]>([])
  const restorePromiseReference = useRef<Promise<void>>(Promise.resolve())
  const restoreFailedReference = useRef(false)
  const onChatCompleteReference = useRef(options.onChatComplete)
  onChatCompleteReference.current = options.onChatComplete
  const queueMessageMutation = useCallback((session: TemporalSession, mutation: MessageMutation): void => {
    pendingMutationsReference.current.push(mutation)
    if (session.rafId !== undefined) {
      return
    }
    session.rafId = requestAnimationFrame(() => {
      session.rafId = undefined
      if (sessionReference.current !== session || epochReference.current !== session.epoch) {
        pendingMutationsReference.current = []
        return
      }
      const mutations = pendingMutationsReference.current
      pendingMutationsReference.current = []
      setMessages(previous => {
        let current = previous
        for (const apply of mutations) {
          current = apply(current)
        }
        return current
      })
    })
  }, [])

  const attachToTurn = useCallback(
    async (turnId: string, epoch: number, optimisticUserMessageId?: string): Promise<void> => {
      const session: TemporalSession = {
        ...createChatStreamSession(),
        epoch,
        lastAppliedSequence: 0,
        optimisticUserMessageId,
        terminal: false,
        turnId,
      }
      sessionReference.current?.controller?.abort()
      sessionReference.current = session
      let reconnectAttempt = 0

      while (!session.terminal && epochReference.current === epoch) {
        const controller = new AbortController()
        session.controller = controller
        let connectionPhase: AgentInteractionPhase = 'reconnecting'
        if (session.lastAppliedSequence === 0) {
          connectionPhase = 'connecting'
        } else if (reconnectAttempt === 0) {
          connectionPhase = 'streaming'
        }
        setPhase(connectionPhase)
        let receivedEvent = false

        try {
          for await (const { event, sequence } of streamChatTurnEvents(
            turnId,
            session.lastAppliedSequence,
            controller.signal
          )) {
            if (sessionReference.current !== session || epochReference.current !== epoch) {
              return
            }
            receivedEvent = true
            reconnectAttempt = 0
            const batchedSetMessages: SetMessages = updater => {
              const mutation: MessageMutation = typeof updater === 'function' ? updater : () => updater
              queueMessageMutation(session, mutation)
            }

            switch (event.type) {
              case 'user_message_saved': {
                const optimisticId = session.optimisticUserMessageId
                const savedMessage = createAgentMessage(event.messageId, event.contents)
                savedMessage.role = 'user'
                queueMessageMutation(session, previous => {
                  const withoutSavedDuplicate = previous.filter(message => message.id !== event.messageId)
                  const optimisticIndex = optimisticId
                    ? withoutSavedDuplicate.findIndex(message => message.id === optimisticId)
                    : -1
                  if (optimisticIndex < 0) {
                    return [...withoutSavedDuplicate, savedMessage]
                  }
                  const next = [...withoutSavedDuplicate]
                  next[optimisticIndex] = savedMessage
                  return next
                })
                session.optimisticUserMessageId = undefined
                break
              }
              case 'thinking': {
                Object.assign(session, applyThinkingEvent(session, event.message, batchedSetMessages))
                break
              }
              case 'text_delta': {
                Object.assign(session, applyTextDelta(session, event.text, batchedSetMessages))
                break
              }
              case 'content_block_added': {
                Object.assign(session, applyContentBlockAdded(session, event.content, batchedSetMessages))
                break
              }
              case 'agent_message_saved': {
                Object.assign(
                  session,
                  applyAgentMessageSaved(session, event.messageId, event.contents, batchedSetMessages)
                )
                break
              }
              case 'error': {
                session.pendingError = event.message
                break
              }
              case 'cancelled': {
                session.pendingError = undefined
                queueMessageMutation(session, previous => removeTemporaryAssistantOutput(previous, session))
                break
              }
              case 'done': {
                session.terminal = true
                if (session.pendingError && !session.savedAgentMessageId) {
                  queueMessageMutation(session, previous => removeTemporaryAssistantOutput(previous, session))
                  setError(session.pendingError)
                  showToast(`Agent error: ${session.pendingError}`, 'error')
                }
                setPhase('idle')
                onChatCompleteReference.current?.()
                break
              }
            }
            session.lastAppliedSequence = sequence
            if (session.terminal) {
              return
            }
          }

          throw new Error('Chat stream ended before a terminal event')
        } catch (streamError) {
          if (controller.signal.aborted || epochReference.current !== epoch) {
            return
          }
          const errorStatus = getChatApiErrorStatus(streamError)
          if (errorStatus === HTTP_STATUS_UNAUTHORIZED || errorStatus === HTTP_STATUS_NOT_FOUND) {
            session.terminal = true
            queueMessageMutation(session, previous => removeTemporaryAssistantOutput(previous, session))
            setError(streamError instanceof Error ? streamError.message : 'Unable to continue the response')
            setPhase('idle')
            onChatCompleteReference.current?.()
            return
          }
          if (streamError instanceof ChatStreamProtocolError) {
            session.terminal = true
            queueMessageMutation(session, previous => removeTemporaryAssistantOutput(previous, session))
            setError(streamError.message)
            setPhase('idle')
            onChatCompleteReference.current?.()
            return
          }
          webLogger.warn('Chat event stream disconnected', { error: streamError, turnId })
          setPhase('reconnecting')
          reconnectAttempt = receivedEvent ? 0 : reconnectAttempt + 1
          try {
            await waitForReconnect(getReconnectDelay(reconnectAttempt), controller.signal)
          } catch {
            return
          }
        }
      }
    },
    [queueMessageMutation]
  )

  useLayoutEffect(() => {
    const epoch = ++epochReference.current
    const previousSession = sessionReference.current
    previousSession?.controller?.abort()
    if (previousSession?.rafId !== undefined) {
      cancelAnimationFrame(previousSession.rafId)
    }
    pendingMutationsReference.current = []
    sessionReference.current = undefined
    restoreFailedReference.current = false
    setMessages(initialMessagesReference.current)
    setError(undefined)

    if (!options.conversationId) {
      setPhase('idle')
      restorePromiseReference.current = Promise.resolve()
      return
    }

    setPhase('restoring')
    const controller = new AbortController()
    const restorePromise = fetchActiveChatTurn(options.conversationId, controller.signal)
      .then(async turn => {
        if (epochReference.current !== epoch) {
          return
        }
        if (!turn) {
          setPhase('idle')
          return
        }
        void attachToTurn(turn.turnId, epoch)
        return undefined
      })
      .catch(restoreError => {
        if (controller.signal.aborted || epochReference.current !== epoch) {
          return
        }
        const message = restoreError instanceof Error ? restoreError.message : 'Unable to restore the active response'
        restoreFailedReference.current = true
        setError(message)
        setPhase('idle')
      })
    restorePromiseReference.current = restorePromise

    return () => {
      controller.abort()
      sessionReference.current?.controller?.abort()
    }
  }, [attachToTurn, options.conversationId, options.viewerKey])

  const isProcessing = phase !== 'idle'

  useLayoutEffect(() => {
    const persistedMessages = initialMessagesReference.current
    if (persistedMessages.length === 0) {
      return
    }
    setMessages(currentMessages =>
      shouldAdoptPersistedMessages(currentMessages, persistedMessages) ? persistedMessages : currentMessages
    )
  }, [initialMessagesSnapshotKey, isProcessing])

  const sendMessage = useCallback(
    async (text: string, sendOptions?: { videoContext?: ChatVideoContext }): Promise<void> => {
      const conversationId = options.conversationId
      if (!conversationId) {
        setError('No conversation loaded')
        showToast('Conversation not ready. Please refresh and try again.', 'error')
        return
      }
      const epoch = epochReference.current
      await restorePromiseReference.current
      if (epochReference.current !== epoch || conversationIdReference.current !== conversationId) {
        return
      }
      if (restoreFailedReference.current) {
        return
      }
      if (sessionReference.current && !sessionReference.current.terminal) {
        setError('Another response is already running')
        return
      }
      setError(undefined)
      const optimisticUserMessageId = generateTemporaryMessageId()
      const optimisticUserMessage: AgentMessage = {
        id: optimisticUserMessageId,
        role: 'user',
        contents: [{ type: 'text', text }],
        timestamp: new Date(),
      }
      setMessages(previous => [...previous, optimisticUserMessage])
      setPhase('creating')
      const requestId = crypto.randomUUID()

      try {
        let turn
        try {
          turn = await createChatTurn({
            requestId,
            conversationId,
            message: text,
            ...(sendOptions?.videoContext ? { videoContext: sendOptions.videoContext } : {}),
          })
        } catch (createError) {
          if (getChatApiErrorStatus(createError) === HTTP_STATUS_CONFLICT) {
            setMessages(previous => previous.filter(message => message.id !== optimisticUserMessageId))
            const activeTurn = await fetchActiveChatTurn(conversationId)
            if (epochReference.current !== epoch) {
              return
            }
            if (!activeTurn) {
              throw createError
            }
            setError('Another response was already running. Reconnected to it.')
            await attachToTurn(activeTurn.turnId, epoch)
            return
          }
          if (epochReference.current !== epoch || conversationIdReference.current !== conversationId) {
            return
          }
          turn = await createChatTurn({
            requestId,
            conversationId,
            message: text,
            ...(sendOptions?.videoContext ? { videoContext: sendOptions.videoContext } : {}),
          })
        }
        if (epochReference.current === epoch) {
          await attachToTurn(turn.turnId, epoch, optimisticUserMessageId)
        }
      } catch (createError) {
        if (epochReference.current !== epoch) {
          return
        }
        setMessages(previous => previous.filter(message => message.id !== optimisticUserMessageId))
        const message = createError instanceof Error ? createError.message : 'Unable to start the response'
        setError(message)
        showToast(message, 'error')
        setPhase('idle')
      }
    },
    [attachToTurn, options.conversationId]
  )

  const stopResponse = useCallback(async (): Promise<void> => {
    const session = sessionReference.current
    if (!session || session.terminal) {
      return
    }
    if (session.stopPromise) {
      return session.stopPromise
    }
    const previousPhase = phase
    setPhase('stopping')
    const stopPromise = cancelChatTurn(session.turnId)
      .then(() => undefined)
      .catch(stopError => {
        if (sessionReference.current !== session || session.terminal) {
          return
        }
        const message = stopError instanceof Error ? stopError.message : 'Unable to stop the response'
        setError(message)
        showToast(message, 'error')
        setPhase(previousPhase === 'stopping' ? 'streaming' : previousPhase)
      })
      .finally(() => {
        if (session.stopPromise === stopPromise) {
          session.stopPromise = undefined
        }
      })
    session.stopPromise = stopPromise
    return stopPromise
  }, [phase])

  const addMessage = useCallback((message: AgentMessage) => setMessages(previous => [...previous, message]), [])
  const clearConversation = useCallback(() => {
    setMessages([])
    setError(undefined)
  }, [])

  return {
    canStop: Boolean(sessionReference.current && !sessionReference.current.terminal),
    messages,
    phase,
    isProcessing,
    isRestoring: phase === 'restoring',
    isStopping: phase === 'stopping',
    error,
    sendMessage,
    stopResponse,
    addMessage,
    clearConversation,
  }
}
